/** @format */

import React, { useState, useEffect, useContext } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, {
  PaginationProvider,
  PaginationListStandalone,
} from "react-bootstrap-table2-paginator";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit";
import ListAdminButtons from "../Common/ListAdminButtons";
import * as dateHelper from "../../Helpers/DateHelper";
import Tooltip from "../../Components/Common/Tooltip";
import { GlobalContext } from "../../Contexts/GlobalContextProvider";
import { AuthContext } from "../../Contexts/AuthContext";
import SpinnerLoading from "../Common/SpinnerLoading";
import DeleteModal from "../Item/DeleteModal";
import { roles } from "../../Config/Roles";
import { rawMaterialService } from "../../Services/rawMaterial.service";
import { itemServices } from "../../Services/item.service";
import ResultModal from "../Common/ResultModal";

const RawMaterialsTab = ({ priceListId }) => {
  const { t: translator } = useTranslation();
  const { roleId } = useContext(AuthContext);
  const { locTenant } = useContext(GlobalContext);
  const { master, admin, support, owner, manager } = roles;
  const { isMobile } = useContext(GlobalContext);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();
  const [update, setUpdate] = useState(new Date());

  const [modal, setModal] = useState({
    title: "",
    text: "",
    show: false,
    btnClass: "",
  });

  useEffect(() => {
    setIsLoading(true);
    rawMaterialService
      .get(Number(locTenant))
      .then((res) => {
        setData(res.data.products || []);
        setIsLoading(false);
      })
      .catch((err) => console.log(err.response));
  }, [update]);

  const { SearchBar } = Search;
  const columns = [
    {
      dataField: "productGuid",
      text: "",
      headerStyle: (colum, colIndex) => {
        return { width: "50px", textAlign: "center" };
      },
      formatter: actionFormatter,
      hidden: ![master, admin, support, owner, manager].includes(Number(roleId))
        ? true
        : false,
    },
    {
      dataField: "name",
      text: translator("TenantDetail-ItemTab-TableHeader-Name"),
      sort: true,
    },
    {
      dataField: "unitOfMeasureName",
      text: translator("TenantDetail-ItemTab-TableHeader-UnitOfMeasure"),
      sort: true,
      hidden: Boolean(isMobile),
    },
  ];
  const handleOnSelect = (row, isSelect) => {
    if (isSelect) {
      setSelectedItems((selectedItems) => [...selectedItems, row.productGuid]);
    } else {
      setSelectedItems((selectedItems) =>
        selectedItems.filter((p) => p !== row.productGuid)
      );
    }
  };

  function actionFormatter(cell, row) {
    return (
      <div className="action">
        <label className="labelCont">
          <input
            type="checkbox"
            checked={selectedItems.find((x) => x === row.productGuid)}
            onChange={(e) => handleOnSelect(row, e.target.checked)}
          />
          <span className="checkmark"></span>
        </label>
      </div>
    );
  }

  const handleToAdd = () => {
    history.push(`/add-raw-material/${priceListId}`);
  };

  const handleToUpdate = () => {
    history.push(`/edit-raw-material/${priceListId}/${selectedItems}`);
  };

  const handleToDetail = () => {
    history.push(`/item-details/${priceListId}/${selectedItems}`);
  };

  const confirmDelete = () => {
    itemServices
      .deleteItems({ items: selectedItems, priceListId })
      .then((res) => {
        console.log("res", res);
        setShowDeleteModal(false);
        res.data.code === 200
          ? setModal({
              ...modal,
              title: translator(
                "TenantDetail-ItemTab-SuccessDeleteModal-Title"
              ),
              text: translator("TenantDetail-ItemTab-SuccessDeleteModal-Text"),
              show: true,
              btnClass: "btn btn-primary",
            })
          : setModal({
              ...modal,
              title: translator(
                "TenantDetail-ItemTab-UnsuccessDeleteModal-Title"
              ),
              text: translator(
                "TenantDetail-ItemTab-UnsuccessDeleteModal-Text"
              ),
              show: true,
              btnClass: "btn btn-primary",
            });
        setUpdate(new Date());
      })
      .catch((err) =>
        setModal({
          ...modal,
          title: translator("Shifts-ShiftsTab-UnsuccesDeleteModal-Title"),
          text: translator("Shifts-ShiftsTab-UnsuccesDeleteModal-Text"),
          show: true,
          btnClass: "btn btn-primary",
        })
      );
  };

  const handleClose = () => {
    setModal({ show: false });
  };

  const handleHideDeleteModal = () => setShowDeleteModal(false);

  const options = { custom: true, totalSize: data?.length, sizePerPage: 50 };

  return (
    <>
      <div className="p-2">
        <div className="container-fluid p-0">
          <div className="card-body pl-0 mb-4">
            <ToolkitProvider
              keyField="productGuid"
              data={data}
              columns={columns}
              search
            >
              {(props) => (
                <div>
                  <SearchBar
                    placeholder={translator("Pos-Tab-Search")}
                    {...props.searchProps}
                  />{" "}
                  <Tooltip text={translator("Tabs-Search")} />
                  <ListAdminButtons
                    selectedItemsLength={selectedItems.length}
                    handleToAdd={
                      [master, admin, support, owner, manager].includes(
                        Number(roleId)
                      )
                        ? handleToAdd
                        : null
                    }
                    handleToDetail={
                      [master, admin, support, owner, manager].includes(
                        Number(roleId)
                      )
                        ? handleToDetail
                        : null
                    }
                    handleToUpdate={
                      [master, admin, support, owner, manager].includes(
                        Number(roleId)
                      )
                        ? handleToUpdate
                        : null
                    }
                    handleDelete={
                      [master, admin, support, owner, manager].includes(
                        Number(roleId)
                      )
                        ? () => setShowDeleteModal(true)
                        : null
                    }
                  />
                  <PaginationProvider pagination={paginationFactory(options)}>
                    {({ paginationProps, paginationTableProps }) => (
                      <div>
                        {isLoading && (
                          <div className="dashboard-card-spinner">
                            <SpinnerLoading />
                          </div>
                        )}
                        {!isLoading && (
                          <BootstrapTable
                            keyField="productGuid"
                            data={data}
                            loading
                            columns={columns}
                            classes={"table-sm"}
                            rowClasses="boostrap-table-row"
                            noDataIndication={translator("No-Data")}
                            {...paginationTableProps}
                            {...props.baseProps}
                          />
                        )}

                        <PaginationListStandalone {...paginationProps} />
                      </div>
                    )}
                  </PaginationProvider>
                </div>
              )}
            </ToolkitProvider>
          </div>
        </div>
      </div>
      <DeleteModal
        show={showDeleteModal}
        handleHide={handleHideDeleteModal}
        title={translator("TenantDetail-ItemTab-DeleteModal-Title")}
        text={translator("TenantDetail-ItemTab-DeleteModal-Text")}
        confirmDelete={confirmDelete}
      />
      <ResultModal
        show={modal.show}
        title={modal.title}
        text={modal.text}
        btnClass={modal.btnClass}
        handleClose={handleClose}
      />
    </>
  );
};

export default RawMaterialsTab;
